import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ExternalLink, Calendar, X } from 'lucide-react';
import TaskDetailModal from '../components/TaskDetailModal';
import { queueTaskEditHandoff } from '../utils/taskEditHandoff';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/DatePicker';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getBrandBadgeClasses } from '../utils/brandColors';
import { TaskIdBadge } from '../components/TaskIdBadge';
import { useTaskOptions, getOptionColor, optionBadgeStyle } from '../utils/taskOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed border-accent palette applied in order to whatever Task Statuses are
// currently active — cosmetic only, not tied to any specific status name so
// renames/reordering/archiving in Task Options always render sensibly.
const COLUMN_ACCENTS = ['border-slate-400', 'border-yellow-400', 'border-blue-400', 'border-orange-400', 'border-green-400', 'border-purple-400', 'border-pink-400'];

// Get current month's start and end dates
const getCurrentMonthDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: firstDay.toISOString().split('T')[0],
    to: lastDay.toISOString().split('T')[0]
  };
};

const KanbanBoard = () => {
  const { token, user } = useAuth();
  const { RAW: TASK_OPTIONS_RAW, TASK_STATUSES } = useTaskOptions();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Initialize with current month
  const currentMonth = getCurrentMonthDates();
  const [fromDate, setFromDate] = useState(currentMonth.from);
  const [toDate, setToDate] = useState(currentMonth.to);
  const [dateField, setDateField] = useState('start_date'); // start_date, created_at, updated_at
  
  // Use centralized permission from AuthContext (cannot drag/drop tasks)
  const { isReadOnly } = useAuth();

  const columns = TASK_STATUSES.map((name, i) => ({
    id: name,
    title: name,
    color: COLUMN_ACCENTS[i % COLUMN_ACCENTS.length],
  }));

  useEffect(() => {
    loadTasks();
    loadUsers();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { your_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task moved!');
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const getCategoryColor = (category) => {
    // Prefer admin-configured colour from Task Options (single source of truth).
    // Falls back to legacy static class map so existing categories keep their
    // familiar look until an admin explicitly customises the hex value.
    const hex = getOptionColor(TASK_OPTIONS_RAW?.categories, category);
    if (hex) return { style: optionBadgeStyle(hex), className: 'border' };
    const colors = {
      'Technical SEO': 'bg-purple-100 text-purple-700 border-purple-200',
      'On-Page': 'bg-blue-100 text-blue-700 border-blue-200',
      'Off-Page': 'bg-pink-100 text-pink-700 border-pink-200',
      'UI/UX Design': 'bg-indigo-100 text-indigo-700 border-indigo-200',
      'SEO': 'bg-green-100 text-green-700 border-green-200',
      'QA/Technical': 'bg-orange-100 text-orange-700 border-orange-200',
      'Google Ads': 'bg-red-100 text-red-700 border-red-200',
      'Meta Ads': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'Others': 'bg-slate-100 text-slate-700 border-slate-200',
      // Legacy categories for backwards compatibility
      'Technical': 'bg-purple-100 text-purple-700 border-purple-200',
      'OnPage': 'bg-blue-100 text-blue-700 border-blue-200',
      'OffPage': 'bg-pink-100 text-pink-700 border-pink-200'
    };
    return { className: colors[category] || colors['Others'] };
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unknown User';
  };

  const isTaskInDateRange = (task) => {
    if (!fromDate && !toDate) return true; // No filters applied
    
    // Get the date field based on selection
    let taskDate = null;
    if (dateField === 'start_date') {
      taskDate = task.start_date ? new Date(task.start_date) : null;
    } else if (dateField === 'created_at') {
      taskDate = task.created_at ? new Date(task.created_at) : null;
    } else if (dateField === 'updated_at') {
      taskDate = task.updated_at ? new Date(task.updated_at) : null;
    }
    
    if (!taskDate) return false; // Exclude tasks without the selected date field when filters are active
    
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    
    if (from && to) {
      return taskDate >= from && taskDate <= to;
    } else if (from) {
      return taskDate >= from;
    } else if (to) {
      return taskDate <= to;
    }
    
    return true;
  };

  const clearDateFilters = () => {
    const currentMonth = getCurrentMonthDates();
    setFromDate(currentMonth.from);
    setToDate(currentMonth.to);
    setDateField('start_date');
    toast.success('Date filters reset to current month');
  };

  const filteredTasks = tasks.filter(isTaskInDateRange);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kanban-board">
      <div className="page-header">
        <h1 className="page-title">Kanban Board</h1>
        <p className="page-subtitle">Drag and drop tasks to update their status</p>
      </div>

      {/* Date Filter Section */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-500" />
            <Label className="text-sm font-medium">Filter by Date Range:</Label>
          </div>
          
          <div className="flex items-center gap-2">
            <Label htmlFor="date-field" className="text-sm">Date Field:</Label>
            <Select value={dateField} onValueChange={setDateField}>
              <SelectTrigger id="date-field" className="w-[140px]" data-testid="date-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="start_date">Start Date</SelectItem>
                <SelectItem value="created_at">Created</SelectItem>
                <SelectItem value="updated_at">Last Updated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label htmlFor="from-date" className="text-sm">From:</Label>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              className="w-auto"
              testId="from-date"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label htmlFor="to-date" className="text-sm">To:</Label>
            <DatePicker
              value={toDate}
              onChange={setToDate}
              className="w-auto"
              testId="to-date"
            />
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={clearDateFilters}
            className="flex items-center gap-1"
            data-testid="reset-filters"
          >
            <X size={14} />
            Reset to Current Month
          </Button>
          
          <div className="text-sm text-slate-600">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {columns.map((column) => {
          const columnTasks = filteredTasks.filter(t => t.your_status === column.id);
          
          return (
            <div
              key={column.id}
              className={`bg-slate-50 rounded-xl p-4 border-t-4 ${column.color}`}
              onDragOver={!isReadOnly ? handleDragOver : undefined}
              onDrop={!isReadOnly ? (e) => handleDrop(e, column.id) : undefined}
              data-testid={`kanban-column-${column.id.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800">{column.title}</h3>
                <span className="text-sm font-semibold text-slate-500 bg-white px-2 py-1 rounded-full">
                  {columnTasks.length}
                </span>
              </div>
              
              <div className="space-y-3 min-h-[500px]">
                {columnTasks.map((task) => (
                  <div
                    key={task.id}
                    draggable={!isReadOnly}
                    onDragStart={!isReadOnly ? (e) => handleDragStart(e, task.id) : undefined}
                    onClick={() => {
                      setSelectedTask(task);
                      setIsDetailOpen(true);
                    }}
                    data-testid={`kanban-card-${task.id}`}
                    className={`bg-white rounded-lg p-4 shadow-sm border border-slate-200 cursor-pointer hover:shadow-md transition-shadow ${isReadOnly ? 'cursor-default' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-start gap-2 flex-wrap">
                        {(() => { const c = getCategoryColor(task.category); return (
                          <span
                            className={`text-xs px-2 py-1 rounded-md font-semibold ${c.className || ''}`}
                            style={c.style}
                          >
                            {task.category}
                          </span>
                        ); })()}
                        {task.brand && (
                          <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getBrandBadgeClasses(task.brand)}`}>
                            {task.brand}
                          </span>
                        )}
                        {task.developer_dependency && (
                          <span className="text-xs px-2 py-1 rounded-md font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                            Dev
                          </span>
                        )}
                      </div>
                      
                      {/* User Avatar and Name */}
                      {task.task_done_by && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-[10px] font-semibold">
                            {getUserName(task.task_done_by).split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                          <span className="text-xs text-slate-600 font-medium">
                            {getUserName(task.task_done_by)}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <h4 className="font-semibold text-slate-900 mb-2 text-sm leading-tight">
                      <TaskIdBadge taskId={task.task_id} className="mr-1.5 align-middle" />
                      {task.task}
                    </h4>
                    
                    {task.output && (
                      <p className="text-xs text-slate-600 mb-2 line-clamp-2">
                        {task.output}
                      </p>
                    )}
                    
                    {task.link && (
                      <a
                        href={task.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={12} /> Link
                      </a>
                    )}
                  </div>
                ))}
                
                {columnTasks.length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    No tasks
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        users={users}
        onTaskUpdated={loadTasks}
        onEdit={(task) => {
          queueTaskEditHandoff(task.id);
          setIsDetailOpen(false);
          navigate('/tasks');
        }}
      />
    </div>
  );
};

export default KanbanBoard;